import "flag-icons/css/flag-icons.min.css";

import { CaretDown, Check } from "@phosphor-icons/react";
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@stylingcv/ui";
import type { Language } from "@stylingcv/utils";
import { cn, languages } from "@stylingcv/utils";
import { useMemo, useState } from "react";

// List of supported language locales - all languages with complete translations
const supportedLanguageCodes = new Set([
  "en-US", // English
  "ar-SA", // Arabic
  "zh-CN", // Chinese Simplified
  "zh-TW", // Chinese Traditional
  "de-DE", // German
  "es-ES", // Spanish
  "fr-FR", // French
  "pt-BR", // Portuguese (Brazilian)
  "pt-PT", // Portuguese
  "it-IT", // Italian
  "nl-NL", // Dutch
  "ru-RU", // Russian
  "ja-JP", // Japanese
  "ko-KR", // Korean
  "hi-IN", // Hindi
  "tr-TR", // Turkish
  "fa-IR", // Persian
  "sv-SE", // Swedish
  "pl-PL", // Polish
  "th-TH", // Thai

  "uk-UA", // Ukrainian
]);

// Language flag mapping
const LANGUAGE_FLAGS: Record<string, string> = {
  "en-US": "gb",
  "ar-SA": "sa",
  "zh-CN": "cn",
  "zh-TW": "tw",
  "de-DE": "de",
  "es-ES": "es",
  "fr-FR": "fr",
  "pt-BR": "br",
  "pt-PT": "pt",
  "it-IT": "it",
  "nl-NL": "nl",
  "ru-RU": "ru",
  "ja-JP": "jp",
  "ko-KR": "kr",
  "hi-IN": "in",
  "tr-TR": "tr",
  "fa-IR": "ir",
  "sv-SE": "se",
  "pl-PL": "pl",
  "th-TH": "th",
  "uk-UA": "ua",
};

// Language display names mapping
const LANGUAGE_NAMES: Record<string, string> = {
  "en-US": "English",
  "ar-SA": "العربية",
  "zh-CN": "中文",
  "zh-TW": "繁體中文",
  "de-DE": "Deutsch",
  "es-ES": "Español",
  "fr-FR": "Français",
  "pt-BR": "Português (BR)",
  "pt-PT": "Português",
  "it-IT": "Italiano",
  "nl-NL": "Nederlands",
  "ru-RU": "Русский",
  "ja-JP": "日本語",
  "ko-KR": "한국어",
  "hi-IN": "हिन्दी",
  "tr-TR": "Türkçe",
  "fa-IR": "فارسی",
  "sv-SE": "Svenska",
  "pl-PL": "Polski",
  "th-TH": "ไทย",

  "uk-UA": "Українська",
};

type Props = {
  value: string;
  onValueChange: (value: string) => void;
};

export const LocaleCombobox = ({ value, onValueChange }: Props) => {
  const filteredLanguages = useMemo(() => {
    const filtered = languages.filter((lang: Language) => supportedLanguageCodes.has(lang.locale));

    const customOrder = [
      "en-US", "zh-CN", "de-DE", "es-ES", "ar-SA",
    ];

    const priorityLanguages: Language[] = [];
    const otherLanguages: Language[] = [];

    for (const locale of customOrder) {
      const lang = filtered.find((l) => l.locale === locale);
      if (lang) priorityLanguages.push(lang);
    }

    for (const lang of filtered) {
      if (!customOrder.includes(lang.locale)) {
        otherLanguages.push(lang);
      }
    }

    otherLanguages.sort((a, b) =>
      (LANGUAGE_NAMES[a.locale] || a.name).localeCompare(LANGUAGE_NAMES[b.locale] || b.name),
    );

    return [...priorityLanguages, ...otherLanguages];
  }, []);

  return (
    <Command shouldFilter={false}>
      <CommandList>
        <CommandGroup>
          <ScrollArea orientation="vertical">
            <div className="max-h-72 py-1">
              {filteredLanguages.map((lang) => (
                <CommandItem
                  key={lang.locale}
                  disabled={false}
                  value={lang.locale.trim()}
                  className="command-item cursor-pointer px-3 py-1.5"
                  onSelect={() => {
                    onValueChange(lang.locale);
                  }}
                >
                  <Check
                    className={cn(
                      "me-1.5 size-3.5 opacity-0",
                      value === lang.locale && "opacity-100",
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <img
                      src={`https://flagcdn.com/w40/${LANGUAGE_FLAGS[lang.locale] || "gb"}.png`}
                      alt={LANGUAGE_NAMES[lang.locale] || lang.name}
                      className="h-3 w-4 object-cover rounded-full"
                    />
                    <span className="text-sm font-medium">
                      {LANGUAGE_NAMES[lang.locale] || lang.name}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </div>
          </ScrollArea>
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export const LocaleComboboxPopover = ({ value, onValueChange }: Props) => {
  const [open, setOpen] = useState(false);

  // Filter to only include supported languages and sort with custom order
  const filteredLanguages = useMemo(() => {
    const filtered = languages.filter((lang: Language) => supportedLanguageCodes.has(lang.locale));

    // Define the exact order we want
    const customOrder = [
      "en-US", // 1. English
      "zh-CN", // 2. Chinese
      "de-DE", // 3. German
      "es-ES", // 4. Spanish
      "ar-SA", // 5. Arabic
    ];

    // Separate languages into priority and others
    const priorityLanguages: Language[] = [];
    const otherLanguages: Language[] = [];

    // First, add languages in custom order
    for (const locale of customOrder) {
      const lang = filtered.find((l) => l.locale === locale);
      if (lang) priorityLanguages.push(lang);
    }

    // Then add remaining languages alphabetically
    for (const lang of filtered) {
      if (!customOrder.includes(lang.locale)) {
        otherLanguages.push(lang);
      }
    }

    // Sort other languages alphabetically
    otherLanguages.sort((a, b) =>
      (LANGUAGE_NAMES[a.locale] || a.name).localeCompare(LANGUAGE_NAMES[b.locale] || b.name),
    );

    return [...priorityLanguages, ...otherLanguages];
  }, []);

  const selected = useMemo(() => {
    return filteredLanguages.find((lang) => lang.locale === value);
  }, [value, filteredLanguages]);

  const onSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          variant="outline"
          aria-expanded={open}
          className="w-full justify-between hover:bg-secondary/20 active:scale-100"
        >
          <div className="flex items-center gap-2">
            <img
              src={`https://flagcdn.com/w40/${LANGUAGE_FLAGS[value] || "gb"}.png`}
              alt={LANGUAGE_NAMES[value] || selected?.name || "Language"}
              className="h-3 w-4 object-cover rounded-full"
            />
            <span className="line-clamp-1 text-left font-normal">
              {LANGUAGE_NAMES[value] || selected?.name}{" "}
              <span className="ms-1 text-xs opacity-50">({value})</span>
            </span>
          </div>
          <CaretDown
            className={cn(
              "ms-2 size-4 shrink-0 rotate-0 opacity-50 transition-transform",
              open && "rotate-180",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[9999] p-0">
        <LocaleCombobox value={value} onValueChange={onSelect} />
      </PopoverContent>
    </Popover>
  );
};
